import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { useSearchParams } from 'react-router-dom';
import ArticleCard from '../components/ArticleCard';
import Spinner from '../components/Spinner';
import { motion, AnimatePresence } from 'framer-motion';
import { FunnelIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

const NewsPage = () => {
    // SSR initial data
    const initialData = typeof window !== 'undefined' ? window.__INITIAL_DATA__ : null;
    const [articles, setArticles] = useState(initialData?.articles || []);
    const [categories, setCategories] = useState([]);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(!initialData?.articles);
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const selectedCategory = searchParams.get('category');
    const selectedTag = searchParams.get('tag');

    useEffect(() => {
        // SSR data bir kez kullanıldıktan sonra temizle (client-side nav için)
        if (typeof window !== 'undefined') window.__INITIAL_DATA__ = null;

        // Eğer SSR'dan data geldiyse ve filter yoksa, tekrar fetch etme
        if (initialData?.articles && !selectedCategory && !selectedTag) {
            // Sadece categories ve tags'i al
            const fetchMeta = async () => {
                try {
                    const [catRes, tagRes] = await Promise.all([
                        axios.get(`${API_BASE_URL}/api/articles/categories`, { params: { pageType: 'News' } }),
                        axios.get(`${API_BASE_URL}/api/articles/tags`, { params: { pageType: 'News' } })
                    ]);
                    setCategories(catRes.data);
                    setTags(tagRes.data);
                } catch (err) {
                    console.error("Error fetching metadata:", err);
                }
            };
            fetchMeta();
            return;
        }
        
        const fetchNews = async () => {
            setLoading(true);
            try {
                const params = { pageType: 'News' };
                if (selectedCategory) params.category = selectedCategory;
                if (selectedTag) params.tag = selectedTag;

                const [catRes, tagRes, articlesRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/articles/categories`, { params: { pageType: 'News' } }),
                    axios.get(`${API_BASE_URL}/api/articles/tags`, { params: { pageType: 'News' } }),
                    axios.get(`${API_BASE_URL}/api/articles`, { params })
                ]);
                setCategories(catRes.data);
                setTags(tagRes.data);
                setArticles(articlesRes.data);
            } catch (err) {
                console.error("Error fetching news:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchNews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategory, selectedTag]);

    const handleCategoryClick = (category) => {
        const newParams = new URLSearchParams(searchParams);
        if (category === selectedCategory || !category) {
            newParams.delete('category');
        } else {
            newParams.set('category', category);
        }
        setSearchParams(newParams);
    };

    const handleTagClick = (tag) => {
        const newParams = new URLSearchParams(searchParams);
        if (tag === selectedTag || !tag) {
            newParams.delete('tag');
        } else {
            newParams.set('tag', tag);
        }
        setSearchParams(newParams);
    };

    const clearAllFilters = () => {
        setSearchParams({});
        setSearchTerm('');
    };

    const filteredArticles = articles.filter(article =>
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const hasActiveFilters = selectedCategory || selectedTag || searchTerm;

    return (
        <>
            <SEO 
                title="Aviation & Space News - Latest Updates | AircoreDynamics"
                description="Latest developments and announcements in aviation, space, and technology. Breaking news from the aerospace industry."
                keywords="aviation news, space news, aerospace announcements, aircraft updates, space technology news"
                image="https://aircoredynamics.com/og-image.png"
                url="/news"
                type="website"
            />
            <div className="container mx-auto px-6 py-8 space-y-6">
                {/* Header */}
                <motion.div 
                    initial={{ opacity: 0, y: -20 }} 
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center space-y-3 py-4"
                >
                    <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-light-primary to-blue-500 dark:from-dark-primary dark:to-cyan-400 bg-clip-text text-transparent">
                        News
                    </h1>
                    <p className="text-lg opacity-70 max-w-2xl mx-auto">Latest developments and announcements in aviation, space, and technology.</p>
                </motion.div>

                {/* Compact Filter Bar */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }} 
                    animate={{ opacity: 1, y: 0 }}
                    className="card p-3 max-w-2xl mx-auto"
                >
                    {/* Search & Filter Row */}
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-3 py-1.5 pl-8 text-sm rounded-lg border border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg focus:outline-none focus:ring-2 focus:ring-light-primary dark:focus:ring-dark-primary"
                            />
                        </div>
                        
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="btn-secondary flex items-center gap-1.5 text-xs px-3 py-1.5 whitespace-nowrap"
                        >
                            <FunnelIcon className="h-3.5 w-3.5" />
                            {hasActiveFilters && <span className="bg-light-primary dark:bg-dark-primary text-white text-[10px] px-1.5 py-0.5 rounded-full">{[selectedCategory, selectedTag].filter(Boolean).length}</span>}
                        </button>
                        
                        {hasActiveFilters && (
                            <button
                                onClick={clearAllFilters}
                                className="text-red-500 hover:text-red-600 p-1.5"
                                title="Clear filters"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        )}
                    </div>

                    {/* Collapsible Filters */}
                    <AnimatePresence>
                        {showFilters && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.15 }}
                                className="overflow-hidden space-y-2 pt-2 mt-2 border-t border-light-border dark:border-dark-border"
                            >
                                {/* Categories */}
                                {categories.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 items-center">
                                        <span className="text-[10px] font-semibold opacity-50 uppercase">Cat:</span>
                                        <button
                                            onClick={() => handleCategoryClick(null)}
                                            className={`px-2 py-0.5 rounded text-[11px] font-medium transition-all ${
                                                !selectedCategory
                                                    ? 'bg-light-primary text-white dark:bg-dark-primary dark:text-dark-bg'
                                                    : 'bg-light-bg dark:bg-dark-bg hover:bg-light-border dark:hover:bg-dark-border'
                                                }`}
                                            >
                                                All
                                        </button>
                                        {categories.map(category => (
                                            <button
                                                key={category}
                                                onClick={() => handleCategoryClick(category)}
                                                className={`px-2 py-0.5 rounded text-[11px] font-medium transition-all ${
                                                    selectedCategory === category
                                                        ? 'bg-light-primary text-white dark:bg-dark-primary dark:text-dark-bg'
                                                        : 'bg-light-bg dark:bg-dark-bg hover:bg-light-border dark:hover:bg-dark-border'
                                                }`}
                                            >
                                                {category}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* Tags */}
                                {tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 items-center">
                                        <span className="text-[10px] font-semibold opacity-50 uppercase">Tags:</span>
                                        <button
                                            onClick={() => handleTagClick(null)}
                                            className={`px-2 py-0.5 rounded-full text-[11px] border transition-all ${
                                                !selectedTag
                                                    ? 'bg-light-primary text-white dark:bg-dark-primary dark:text-dark-bg border-transparent'
                                                    : 'border-light-border dark:border-dark-border hover:border-light-primary dark:hover:border-dark-primary'
                                            }`}
                                        >
                                            All
                                        </button>
                                        {tags.map(tag => (
                                            <button
                                                key={tag}
                                                onClick={() => handleTagClick(tag)}
                                                className={`px-2 py-0.5 rounded-full text-[11px] border transition-all ${
                                                    selectedTag === tag
                                                        ? 'bg-light-primary text-white dark:bg-dark-primary dark:text-dark-bg border-transparent'
                                                        : 'border-light-border dark:border-dark-border hover:border-light-primary dark:hover:border-dark-primary'
                                                }`}
                                            >
                                                #{tag}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>

                {/* Results Count */}
                {!loading && (
                    <div className="text-xs opacity-60">
                        {filteredArticles.length} result{filteredArticles.length !== 1 ? 's' : ''}
                    </div>
                )}

                {/* Articles Grid */}
                {loading ? (
                    <Spinner />
                ) : filteredArticles.length > 0 ? (
                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                        initial="hidden"
                        animate="visible"
                        variants={{
                            visible: {
                                transition: { staggerChildren: 0.1 }
                            }
                        }}
                    >
                        {filteredArticles.map(article => (
                            <motion.div
                                key={article._id}
                                variants={{
                                    hidden: { opacity: 0, y: 20 },
                                    visible: { opacity: 1, y: 0 }
                                }}
                                transition={{ duration: 0.3 }}
                            >
                                <ArticleCard article={article} />
                            </motion.div>
                        ))}
                    </motion.div>
                ) : (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="card p-12 text-center"
                    >
                        <div className="text-5xl mb-4">📰</div>
                        <h3 className="text-xl font-semibold mb-2">No News Found</h3>
                        <p className="text-sm opacity-70 mb-4">
                            {hasActiveFilters 
                                ? 'No news match your filters. Try adjusting them.'
                                : 'No news have been published yet.'}
                        </p>
                        {hasActiveFilters && (
                            <button onClick={clearAllFilters} className="btn-secondary text-sm">
                                Clear Filters
                            </button>
                        )}
                    </motion.div>
                )}
            </div>
        </>
    );
};

export default NewsPage;